"use client";

import { Drawer, DrawerBody, DrawerContent } from "@dc/ui/components";
import { cn } from "@dc/ui/lib/utils";
import { TRANSITION_EASINGS } from "@heroui/framer-utils";
import type { ModalProps } from "@heroui/react";
import { type ComponentProps, forwardRef, useEffect, useMemo, useRef } from "react";

// When to show the sidebar on the layout
type SidebarBreakpoint = "hidden" | "sm" | "md" | "lg";

const breakpointClasses: Record<SidebarBreakpoint, string> = {
  hidden: "hidden",
  sm: "hidden sm:flex",
  md: "hidden md:flex",
  lg: "hidden lg:flex",
};

export const SidebarDrawer = forwardRef<
  HTMLDivElement,
  ModalProps & {
    sidebarWidth?: number;
    sidebarPlacement?: "left" | "right";
    motionProps?: ComponentProps<typeof Drawer>["motionProps"];
    sidebarBreakpoint?: SidebarBreakpoint;
  }
>(
  (
    {
      children,
      className,
      onOpenChange,
      isOpen,
      sidebarWidth = 300,
      classNames = {},
      sidebarPlacement = "left",
      motionProps: drawerMotionProps,
      sidebarBreakpoint = "sm",
      ...props
    },
    ref,
  ) => {
    // Workaround for iOS Safari: HeroUI's backdrop calls preventDefault() on
    // pointerdown, which suppresses the click event that React Aria's
    // useInteractOutside needs to dismiss the modal. We listen for touchend
    // at the document level and close when the touch target is outside the
    // drawer content (i.e. on the backdrop).
    const contentRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
      if (!isOpen || !onOpenChange) return;

      const handleTouchEnd = (e: TouchEvent) => {
        const target = e.target as HTMLElement;
        if (contentRef.current?.contains(target)) return;
        // Don't close if a child overlay (e.g. popover) is open — its content
        // renders in a portal outside the drawer DOM, so touches inside it
        // (including scroll gestures) would otherwise dismiss the drawer.
        if (document.documentElement.dataset.overlayOpen) return;
        onOpenChange(false);
      };

      document.addEventListener("touchend", handleTouchEnd, true);
      return () => {
        document.removeEventListener("touchend", handleTouchEnd, true);
      };
    }, [isOpen, onOpenChange]);

    const motionProps = useMemo(() => {
      if (!!drawerMotionProps && typeof drawerMotionProps === "object") {
        return drawerMotionProps;
      }

      return {
        variants: {
          enter: {
            x: 0,
            transition: {
              x: {
                duration: 0.3,
                ease: TRANSITION_EASINGS.easeOut,
              },
            },
          },
          exit: {
            x: sidebarPlacement === "left" ? -sidebarWidth : sidebarWidth,
            transition: {
              x: {
                duration: 0.2,
                ease: TRANSITION_EASINGS.easeOut,
              },
            },
          },
        },
      };
    }, [sidebarWidth, sidebarPlacement, drawerMotionProps]);

    return (
      <>
        <Drawer
          ref={ref}
          {...props}
          classNames={{
            ...classNames,
            wrapper: cn("w-(--sidebar-width)!", classNames?.wrapper, {
              "items-start! justify-start!": sidebarPlacement === "left",
              "items-end! justify-end!": sidebarPlacement === "right",
            }),
            base: cn("m-0! h-full max-h-full w-(--sidebar-width) p-0", classNames?.base, className, {
              "justify-start! inset-y-0 left-0 max-h-none rounded-l-none": sidebarPlacement === "left",
              "justify-end! inset-y-0 right-0 max-h-none rounded-r-none": sidebarPlacement === "right",
            }),
            body: cn("p-0", classNames?.body),
            closeButton: cn("z-50", classNames?.closeButton),
          }}
          isOpen={isOpen}
          motionProps={motionProps}
          radius="none"
          scrollBehavior="inside"
          style={{
            // @ts-ignore
            "--sidebar-width": `${sidebarWidth}px`,
          }}
          onOpenChange={onOpenChange}
        >
          <DrawerContent>
            <DrawerBody>
              <div ref={contentRef} className="h-full">
                {children}
              </div>
            </DrawerBody>
          </DrawerContent>
        </Drawer>
        <div
          className={cn(
            "h-full max-w-(--sidebar-width) overflow-y-auto overflow-x-hidden bg-transparent!",
            breakpointClasses[sidebarBreakpoint],
            className,
          )}
        >
          {children}
        </div>
      </>
    );
  },
);
